import type { FixtureNode } from '../core/FixtureNode.js';
import type { FixtureExport } from '../core/fixtureApi.js';
import type { ExceptionError, RenderEvent, RenderReport } from '../core/ErrorInfo.js';
import type { ExplorerRuntime, FixtureModules } from '../core/index.js';
import { createFixtureTree } from '../core/FixtureNode.js';
import { ComponentRendering } from '../core/ComponentRenderer.js';
import { runtimeVersion } from '../runtimeVersion.js';

declare global {
	interface Window {
		__componentExplorer__?: HeadlessApi;
	}
}

interface HeadlessApi {
	readonly sessionId: string;
	readonly updateVersion: number;
	readonly runtimeVersion: string;
	listFixtures(): FlatFixture[];
	renderFixture(fixtureId: string): Promise<RenderReport>;
}

interface FlatFixture {
	readonly fixtureId: string;
	readonly fixtureName: string;
	readonly groupPath: string[];
	readonly background: 'light' | 'dark';
	readonly labels: readonly string[];
}

export class HeadlessMode implements ExplorerRuntime {
	readonly sessionId = Math.random().toString(36).slice(2);
	private _updateVersion = 0;
	private _root: FixtureNode;
	private _fixtureModules: FixtureModules;
	private _currentRendering: ComponentRendering | undefined;
	private _currentContainer: HTMLElement | undefined;

	constructor(
		private readonly _containerElement: HTMLElement,
		fixtureModules: FixtureModules,
	) {
		this._fixtureModules = fixtureModules;
		this._root = this._buildTree(fixtureModules);
		this._installGlobalApi();

		const count = this._flattenFixtures(this._root).length;
		console.log('[headless] Initialized, sessionId:', this.sessionId, 'version:', this._updateVersion, 'fixtures:', count);
	}

	get updateVersion(): number {
		return this._updateVersion;
	}

	updateFixtures(fixtureModules: FixtureModules): void {
		this._fixtureModules = fixtureModules;
		this._root = this._buildTree(fixtureModules);
		this._updateVersion++;
		const count = this._flattenFixtures(this._root).length;
		console.log('[headless] Registry rebuilt, version:', this._updateVersion, 'fixtures:', count);
	}

	bumpVersion(): void {
		this._updateVersion++;
		console.log('[headless] Version bumped:', this._updateVersion);
	}

	listFixtures(): FlatFixture[] {
		return this._flattenFixtures(this._root);
	}

	async renderFixture(fixtureId: string): Promise<RenderReport> {
		const node = this._findFixtureNode(this._root, fixtureId);
		if (!node || !node.component) {
			throw new Error('Fixture not found: ' + fixtureId);
		}

		if (this._currentRendering) {
			this._currentRendering.dispose();
			this._currentRendering = undefined;
		}
		if (this._currentContainer) {
			this._currentContainer.remove();
			this._currentContainer = undefined;
		}

		const container = document.createElement('div');
		container.style.display = 'inline-block';
		this._containerElement.appendChild(container);
		this._currentContainer = container;

		const def = node.component;

		const events: RenderEvent[] = [];
		let error: ExceptionError | undefined;

		const setError = (e: ExceptionError) => {
			if (!error) { error = e; }
		};

		const originalError = console.error;
		const originalWarn = console.warn;
		const originalLog = console.log;
		console.error = (...args: unknown[]) => {
			events.push({ type: 'console.error', message: args.map(String).join(' ') });
			originalError.apply(console, args);
		};
		console.warn = (...args: unknown[]) => {
			events.push({ type: 'console.warn', message: args.map(String).join(' ') });
			originalWarn.apply(console, args);
		};
		console.log = (...args: unknown[]) => {
			events.push({ type: 'console.log', message: args.map(String).join(' ') });
			originalLog.apply(console, args);
		};

		const errorHandler = (event: ErrorEvent) => {
			events.push({ type: 'window.error', message: event.message });
		};
		const rejectionHandler = (event: PromiseRejectionEvent) => {
			const reason = event.reason;
			const message = reason instanceof Error ? reason.message : String(reason);
			events.push({ type: 'window.unhandledrejection', message });
		};
		window.addEventListener('error', errorHandler);
		window.addEventListener('unhandledrejection', rejectionHandler);

		let resultData: unknown;
		try {
			const rendering = new ComponentRendering(container, def);
			this._currentRendering = rendering;

			const TIMEOUT_MS = 3000;
			const renderTimeout = new Promise<never>((_, reject) => setTimeout(() => reject(new Error(`Fixture timed out after ${TIMEOUT_MS}ms`)), TIMEOUT_MS));
			const result = await Promise.race([rendering.waitForRendering(), renderTimeout]);
			resultData = result?.data;
		} catch (e) {
			const err = e instanceof Error ? e : new Error(String(e));
			setError({ message: err.message, stack: err.stack });
		} finally {
			console.error = originalError;
			console.warn = originalWarn;
			console.log = originalLog;
		}

		await new Promise<void>(resolve => requestAnimationFrame(() => requestAnimationFrame(() => resolve())));

		window.removeEventListener('error', errorHandler);
		window.removeEventListener('unhandledrejection', rejectionHandler);

		const hasError = !!error || events.some(e => e.type !== 'console.log' && e.type !== 'console.warn');
		return { hasError, error, events, resultData };
	}

	private _buildTree(fixtureModules: FixtureModules): FixtureNode {
		const fixtures = new Map<string, FixtureExport>();
		for (const [path, module] of Object.entries(fixtureModules)) {
			const fixture = module.default;
			if (fixture && typeof fixture === 'object') {
				fixtures.set(path, fixture as FixtureExport);
			}
		}
		return createFixtureTree(fixtures);
	}

	private _flattenFixtures(node: FixtureNode, groupPath: string[] = [], inheritedLabels: readonly string[] = []): FlatFixture[] {
		const effectiveLabels = inheritedLabels.length > 0 && node.labels.length > 0
			? [...new Set([...inheritedLabels, ...node.labels])]
			: node.labels.length > 0 ? [...node.labels]
			: [...inheritedLabels];
		const results: FlatFixture[] = [];
		if (node.type === 'component') {
			results.push({ fixtureId: node.id.value, fixtureName: node.name, groupPath, background: node.component?.background ?? 'light', labels: effectiveLabels });
		}
		for (const child of node.children ?? []) {
			const nextPath = node.id.value ? [...groupPath, node.name] : groupPath;
			results.push(...this._flattenFixtures(child, nextPath, effectiveLabels));
		}
		return results;
	}

	private _findFixtureNode(node: FixtureNode, fixtureId: string): FixtureNode | undefined {
		if (node.id.value === fixtureId) return node;
		for (const child of node.children ?? []) {
			const found = this._findFixtureNode(child, fixtureId);
			if (found) return found;
		}
		return undefined;
	}

	private _installGlobalApi(): void {
		const self = this;
		const api: HeadlessApi = {
			sessionId: this.sessionId,
			get updateVersion() { return self._updateVersion; },
			runtimeVersion: runtimeVersion.toString(),
			listFixtures: () => this.listFixtures(),
			renderFixture: (id) => this.renderFixture(id),
		};
		window.__componentExplorer__ = api;
	}
}
